import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { NotifierContextProvider } from "react-headless-notifier";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ReactDOM from "react-dom/client";

import "./i18n";

import AuthenticatedLayout from "./layouts/AuthenticatedLayout";
import PublicLayout from "./layouts/PublicLayout";
import Account from "./pages/Accounts/Account";
import AccountList from "./pages/Accounts/AccountsList";
import AdminMenu from "./pages/Admin/AdminMenu";
import AwaitingInventory from "./pages/AwaitingInventory/AwaitingInventory";
import PartsVehicleLookup from "./pages/AwaitingInventory/PartsVehicleLookup";
import Calendar from "./pages/Calendar/Calendar";
import OldTripManager from "./pages/Calendar/OldTripManager";
import TripManager from "./pages/Calendar/TripManager";
import Chat from "./pages/Chat/Chat";
import CategoryEditor from "./pages/CategoryEditor/CategoryEditor";
import EditCategory from "./pages/CategoryEditor/EditCategory";
import Contact from "./pages/Contacts/Contact";
import ContactList from "./pages/Contacts/ContactList";
import Dashboard from "./pages/Dashboard/Dashboard";
import DriverLead from "./pages/Driver/DriverLead";
import FindLead from "./pages/Driver/FindLead";
import DriverTracking from "./pages/DriverTracking/DriverTracking";
import BulkList from "./pages/EbayListings/BulkList";
import EbayListings from "./pages/EbayListings/EbayListings";
import EbayUnlisted from "./pages/EbayListings/EbayUnlisted";
import ViewListing from "./pages/EbayListings/ViewListing";
import EbayMessages from "./pages/EbayMessages/EbayMessages";
import PartProMessages from "./pages/PartProMessages/PartProMessages";
import EbayOrders from "./pages/EbayOrders/EbayOrders";
import EbayReturns from "./pages/EbayReturns/EbayReturns";
import ErrorBoundry from "./pages/Error/ErrorBoundry";
import Exports from "./pages/Exports/Exports";
import EbayIntegration from "./pages/Intergrations/Ebay";
import TellerooIntegration from "./pages/Intergrations/Telleroo";
import ShopifyIntegration from "./pages/Intergrations/Shopify";
import PartProIntegration from "./pages/Intergrations/PartPro";
import Jobs from "./pages/Jobs/Jobs";
import Job from "./pages/Jobs/ViewJob";
import ProductionPlanner from "./pages/ProductionPlanner/ProductionPlanner";
import CreateLead from "./pages/Leads/CreateLead";
import CreateLeadMultipleVehicles from "./pages/Leads/CreateLeadMultipleVehicles";
import Lead from "./pages/Leads/Lead";
import Leads from "./pages/Leads/LeadList";
import DeletedLeads from "./pages/Leads/DeletedLeads";
import Quotes from "./pages/Leads/Quotes";
import RequireAuth from "./pages/Login/RequireAuth";
import PartCategoryRequiredImagesManager from "./pages/PartCategoryRequiredImages/PartCategoryRequireImagesManager";
import PartsChecker from "./pages/PartsChecker/PartsChecker";
import PartsEditor from "./pages/PartsEditor/PartsEditor";
import Payments from "./pages/Payments/Payments";
import CustomerWebForm from "./pages/Public Pages/CustomerWebForm";
import Schedule from "./pages/Schedule/Schedule";
import Search from "./pages/Search/Search";
import GeneralSettings from "./pages/Settings/General";
import MailSettings from "./pages/Settings/Mail";
import Settings from "./pages/Settings/Settings";
import Stock from "./pages/Stock/Stock";
import StockQc from "./pages/Stock/StockQc";
import ViewStock from "./pages/Stock/ViewStock";
import { Test } from "./pages/Test/Test";
import CreateTicket from "./pages/Tickets/CreateTicket";
import Tickets from "./pages/Tickets/Tickets";
import ViewTicket from "./pages/Tickets/ViewTicket";
import Vehicle from "./pages/Vehicles/Vehicle";
import VehicleList from "./pages/Vehicles/VehicleList";
import VehicleBoard from "./pages/Vehicles/VehicleBoard";
import EditVehicle from "./pages/Vehicles/EditVehicle";
import VehiclesAwaitingArrival from "./pages/VehiclesAwaitingArrival/VehiclesAwaitingArrival";
import VehiclesCollected from "./pages/VehiclesCollected/VehiclesCollected";
import IFrame from "./pages/Widget/IFrame";
import * as Sentry from "@sentry/react";
import { sentryDSN, sentryReplaySampleRate, sentryReplayOnErrorSampleRate } from "./config";
import CreateVehicle from "./pages/Vehicles/CreateVehicle";

import "./index.css";
import Reports from "./pages/Reports/Reports";
import Performance from "./pages/Performance/Performance";
import ViewReport from "./components/reports/ViewReport";
import InvoiceList from "./components/invoices/InvoiceList";
import Invoices from "./pages/Invoices/Invoices";
import ViewInvoice from "./pages/Invoices/ViewInvoice";
import RecentlyListed from "./pages/EbayListings/RecentlyListed";
import DropOff from "./pages/DropOff/DropOff";
import DepollutionList from "./pages/Depollution/DepollutionList";
import TriageList from "./pages/Triage/TriageList";
import Triage from "./pages/Triage/Triage";
import Depollution from "./pages/Depollution/Depollution";
import NotFound from "./pages/Error/NotFound";
import ProductionDashboard from "./pages/Production/ProductionDashboard";
import StationManager from "./pages/Production/StationManager";
import Core from "./pages/Core/Core";
import WorkOrders from "./pages/WorkOrders/WorkOrders";
import ShopifySyncDebug from "./pages/Debug/ShopifySyncDebug";
import ShopifyRepairDebug from "./pages/Debug/ShopifyRepairDebug";
import ShopifyEbayBackfillDebug from "./pages/Debug/ShopifyEbayBackfillDebug";
import isLocalDebug from "./utils/isLocalDebug";

Sentry.init({
  dsn: sentryDSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // integrations: [
  //   Sentry.replayIntegration()
  // ],
  // // Session Replay
  // replaysSessionSampleRate: sentryReplaySampleRate, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // replaysOnErrorSampleRate: sentryReplayOnErrorSampleRate // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const widgetPath = (url: string) => {
  return `https://app.recyclingsoftware.co.uk/${url}?embed=true`;
  // return `http://localhost:3000/${url}?embed=true`;
};

const showDebugRoutes = isLocalDebug();

const router = createBrowserRouter(
  [
    {
      element: <PublicLayout />,
      children: [
        {
          path: "/customer-info",
          element: <CustomerWebForm />,
          errorElement: <ErrorBoundry />,
        },
        {
          path: "/integrations",
          children: [
            {
              path: "telleroo",
              element: <TellerooIntegration />,
            },
            {
              path: "ebay",
              element: <EbayIntegration />,
            },
            {
              path: "shopify",
              element: <ShopifyIntegration />,
            },
            {
              path: "partpro",
              element: <PartProIntegration />,
            },
          ],
        },
        {
          path: "*",
          element: <NotFound />
        },
      ],
    },
    {
      path: "/",
      element: <AuthenticatedLayout />,
      errorElement: <ErrorBoundry />,
      children: [
        {
          path: "*",
          element: <NotFound />
        },
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="dashboard"
            />
          ),
          children: [{ index: true, element: <Dashboard /> }],
        },
        {
          //Admin only routes
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: false, isMechanic: false, isDriver: false, isExporter: false }}
              requiredFeature="admin"
            />
          ),
          children: [
            ...(showDebugRoutes ? [
              {
                path: "debug",
                children: [
                  {
                    path: "shopify-sync",
                    element: <ShopifySyncDebug />,
                  },
                  {
                    path: "shopify-repair",
                    element: <ShopifyRepairDebug />,
                  },
                  {
                    path: "shopify-ebay-backfill",
                    element: <ShopifyEbayBackfillDebug />,
                  },
                ],
              },
            ] : []),
            {
              path: "admin",
              element: <AdminMenu />,
              children: [
                {
                  index: true,
                  element: <AdminMenu />,
                },
                {
                  path: ":section",
                  element: <AdminMenu />,
                },
              ],
            },
            {
              path: "parts-editor",
              children: [
                {
                  index: true,
                  element: <PartsEditor />,
                },
                {
                  path: ":categoryId",
                  element: <PartsEditor />,
                },
              ],
            },
            {
              path: "ebay-messages",
              children: [
                {
                  index: true,
                  element: <EbayMessages />,
                },
              ],
            },
            {
              path: "partpro-messages",
              children: [
                {
                  index: true,
                  element: <PartProMessages />,
                },
              ],
            },
            {
              path: "category-editor",
              children: [
                {
                  index: true,
                  element: <CategoryEditor />,
                },
                {
                  path: ":categoryId",
                  element: <EditCategory />,
                },
              ],
            },
            {
              path: "category-images",
              children: [
                {
                  index: true,
                  element: <PartCategoryRequiredImagesManager />,
                },
              ],
            },
            {
              path: "reports",
              children: [
                {
                  index: true,
                  element: <Reports />,
                },
                {
                  path: ":reportId",
                  element: <ViewReport />,
                }
              ],
            },
            {
              path: "performance",
              children: [
                {
                  index: true,
                  element: <Performance />,
                }
              ],
            },
            {
              path: "test",
              children: [
                {
                  index: true,
                  element: <Test />,
                },
              ],
            },
          ],
        },
        {
          //Tickets routes for all authenticated users (support is not feature-gated)
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: true, isExporter: true }}
            />
          ),
          children: [
            {
              path: "tickets",
              children: [
                {
                  index: true,
                  element: <Tickets />,
                },
                {
                  path: "new",
                  element: <CreateTicket />,
                },
                {
                  path: ":ticketId",
                  element: <ViewTicket />,
                },
              ],
            },
          ],
        },
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: true, isExporter: true }}
              requiredFeature="chat"
            />
          ),
          children: [
            {
              path: "chat",
              element: <Chat />,
            },
          ],
        },
        {
          //Leads
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="leads"
            />
          ),
          children: [
            {
              path: "leads",
              children: [
                {
                  index: true,
                  element: <Leads />,
                },
                {
                  path: "create",
                  element: <CreateLead />,
                },
                {
                  path: "create-multiple",
                  element: <CreateLeadMultipleVehicles />,
                },
                {
                  path: "deleted",
                  element: <DeletedLeads />,
                },
                {
                  path: ":leadId",
                  element: <Lead />,
                  children: [
                    {
                      path: ":tab",
                      element: <Lead />,
                    },
                  ],
                },
              ],
            },
            {
              path: "bookings",
              children: [
                {
                  index: true,
                  element: <Leads />,
                },
                {
                  path: ":leadId",
                  element: <Lead />,
                  children: [
                    {
                      path: ":tab",
                      element: <Lead />,
                    },
                  ],
                },
                {
                  path: "create",
                  element: <CreateLead />,
                },
                {
                  path: "create-multiple",
                  element: <CreateLeadMultipleVehicles />,
                },
              ],
            },
            {
              path: "quotes",
              element: <Quotes />,
            },
            {
              path: "vehicles-collected",
              element: <VehiclesCollected />,
            },
            {
              path: "calendar",
              element: <Calendar />,
            },
            {
              path: "trip-manager",
              element: <TripManager />,
            },
            {
              path: "old-trip-manager",
              element: <OldTripManager />,
            },
            {
              path: "payments",
              element: <Payments />,
            },
            {
              path: "schedule",
              element: <Schedule />,
            },
          ],
        },
        {
          // Drop Offs — Leads for standard customers, Ops Management for TME.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="leads"
              requiredFeatureTme="opsManagement"
            />
          ),
          children: [
            {
              path: "drop-offs",
              element: <DropOff />,
            },
          ],
        },
        {
          // Vehicles list
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="vehicles"
            />
          ),
          children: [
            {
              path: "vehicles",
              children: [
                {
                  index: true,
                  element: <VehicleList />,
                },
                {
                  path: "create",
                  element: <CreateVehicle />,
                },
                {
                  path: ":vehicleId",
                  element: <Vehicle />,
                },
                {
                  path: ":vehicleId/edit",
                  element: <EditVehicle />,
                },
              ],
            },
            {
              path: "parts-manager",
              children: [
                {
                  index: true,
                  element: <AwaitingInventory />,
                },
                {
                  path: "vehicle-lookup",
                  element: <PartsVehicleLookup />,
                },
              ],
            },
            {
              path: "jobs",
              children: [
                {
                  index: true,
                  element: <Jobs />,
                },
                {
                  path: ":jobId",
                  element: <Job />,
                },
              ],
            },
          ],
        },
        {
          // Ops Management (TME) — falls back to Vehicles for non-TME.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="vehicles"
              requiredFeatureTme="opsManagement"
            />
          ),
          children: [
            {
              path: "production-dashboard",
              element: <ProductionDashboard />,
            },
            {
              path: "vehicle-board",
              element: <VehicleBoard />,
            },
            {
              path: "production-planner",
              children: [
                {
                  index: true,
                  element: <ProductionPlanner />,
                },
              ],
            },
          ],
        },
        {
          // Dismantling (TME) — falls back to Vehicles for non-TME.
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="vehicles"
              requiredFeatureTme="dismantling"
            />
          ),
          children: [
            {
              path: "station-manager",
              element: <StationManager />,
            },
            {
              path: "depollution",
              children: [
                {
                  index: true,
                  element: <DepollutionList />,
                },
                {
                  path: ":depollutionId",
                  element: <Depollution />,
                },
              ],
            },
            {
              path: "triage",
              children: [
                {
                  index: true,
                  element: <TriageList />,
                },
                {
                  path: ":triageId",
                  element: <Triage />,
                },
              ],
            },
          ],
        },
        {
          //Parts
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="parts"
            />
          ),
          children: [
            {
              path: "work-orders",
              element: <WorkOrders />,
            },
            {
              path: "awaiting-arrival",
              element: <VehiclesAwaitingArrival />,
            },
            {
              path: "parts-checker",
              element: <PartsChecker />,
            },
            {
              path: "stock",
              children: [
                {
                  index: true,
                  element: <Stock />,
                },
                {
                  path: ":partId",
                  element: <ViewStock />,
                },
              ],
            },
            {
              path: "core",
              children: [
                {
                  index: true,
                  element: <Core />,
                },
                {
                  path: ":partId",
                  element: <ViewStock />,
                },
              ],
            },
            {
              path: "stock-qc",
              children: [
                {
                  index: true,
                  element: <StockQc />,
                },
              ]
            },
          ],
        },
        {
          //Order Management
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="orderManagement"
            />
          ),
          children: [
            {
              path: "ebay-listings",
              children: [
                {
                  index: true,
                  element: <EbayListings />,
                },
                {
                  path: ":partId",
                  element: <ViewListing />,
                },
                {
                  path: "bulk-list/:batchId",
                  element: <BulkList />,
                },
              ],
            },
            {
              path: "ebay-unlisted",
              children: [
                {
                  index: true,
                  element: <EbayUnlisted />,
                },
                {
                  path: ":partId",
                  element: <ViewListing />,
                },
              ],
            },
            {
              path: "recently-listed",
              children: [
                {
                  index: true,
                  element: <RecentlyListed />,
                },
                {
                  path: ":partId",
                  element: <ViewListing />,
                },
              ],
            },
            {
              path: "ebay-orders",
              children: [
                {
                  index: true,
                  element: <EbayOrders />,
                },
              ],
            },
            {
              path: "ebay-returns",
              children: [
                {
                  index: true,
                  element: <EbayReturns />,
                },
              ],
            },
          ],
        },
        {
          //Accounts
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="accounts"
            />
          ),
          children: [
            {
              path: "contacts",
              children: [
                {
                  index: true,
                  element: <ContactList />,
                },
                {
                  path: ":contactId",
                  element: <Contact />,
                },
              ],
            },
            {
              path: "accounts",
              children: [
                {
                  index: true,
                  element: <AccountList />
                },
                {
                  path: ':accountId',
                  element: <Account />
                }
              ]
            },
            {
              path: "invoices",
              children: [
                {
                  index: true,
                  element: <Invoices />
                },
                {
                  path: ":invoiceId",
                  element: <ViewInvoice />
                }
              ]
            },
          ],
        },
        {
          //Admin-scoped extras that live under staff role checks historically
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="admin"
            />
          ),
          children: [
            {
              path: "driver-tracking",
              element: <DriverTracking />,
            },
            {
              path: "settings",
              element: <Settings />,
              children: [
                {
                  index: true,
                  element: <GeneralSettings />,
                },
                {
                  path: "mail-importer",
                  element: <MailSettings />,
                },
              ],
            },
          ],
        },
        {
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="dashboard"
            />
          ),
          children: [
            {
              path: "search",
              element: <Search />,
            },
          ],
        },
        {
          //Widget
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: false, isExporter: false }}
              requiredFeature="widget"
            />
          ),
          children: [
            {
              path: "widget-dashboard",
              element: <IFrame src={widgetPath("")} />,
            },
            {
              path: "widget-calculator",
              element: <IFrame src={widgetPath("calculate")} />,
            },
            {
              path: "widget-postcodes",
              element: <IFrame src={widgetPath("scrap-price")} />,
            },
            {
              path: "widget-suppliers",
              element: <IFrame src={widgetPath("suppliers")} />,
            },
            {
              path: "sellavehicle",
              element: <IFrame src={widgetPath("sellavehicle")} />,
            },
            {
              path: "widget-config",
              element: <IFrame src={widgetPath("price-settings")} />,
            },
            {
              path: "abandoned-quotes",
              element: <IFrame src={widgetPath("abandoned-quotes")} />,
            },
          ],
        },
        {
          //Driver routes
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: false, isMechanic: false, isDriver: true, isExporter: false }}
              requiredFeature="findLead"
            />
          ),
          children: [
            {
              path: "find-lead",
              element: <FindLead />,
            },
            {
              path: "/driver/lead/:leadId",
              element: <DriverLead />,
            },
          ],
        },
        {
          //Exporter / exports (parts scope)
          element: (
            <RequireAuth
              allowedRoles={{ isAdmin: true, isStaff: true, isMechanic: true, isDriver: true, isExporter: true }}
              requiredFeature="parts"
            />
          ),
          children: [
            {
              path: "exports",
              element: <Exports />,
            }
          ]
        }
      ],
    },
  ],
  {
    basename: process.env.PUBLIC_URL,
  }
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60 * 2, //2 minutes
    },
  },
});


//Moved some of the providers to layouts to separate public and private routes

root.render(
  <QueryClientProvider client={queryClient}>
    <NotifierContextProvider
      config={{
        max: 5,
        position: "topRight",
      }}
    >
      <DndProvider backend={HTML5Backend}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </DndProvider>
    </NotifierContextProvider>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
